'use client';

import { Star, StarHalf, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Container from '@/components/ui/container';
import { cn, parseHeading } from '@/lib/utils';
import { useGoogleReviews } from '@/context/GoogleReviewsContext';

// Theme configuration based on background color
const getTheme = (background?: string) => {
	switch (background) {
		case 'blue':
			return {
				section: 'bg-blue',
				heading: 'text-white',
				subtext: 'text-white/80',
				card: 'bg-white/10 border-white/20',
				cardText: 'text-white',
				cardSubtext: 'text-white/70',
				link: 'text-white hover:text-white/80',
				star: 'text-yellow-400',
				arrow: 'bg-white/20 hover:bg-white/30 text-white',
				arrowDisabled: 'bg-white/10 text-white/30',
			};
		case 'orange':
			return {
				section: 'bg-orange',
				heading: 'text-white',
				subtext: 'text-white/80',
				card: 'bg-white/10 border-white/20',
				cardText: 'text-white',
				cardSubtext: 'text-white/70',
				link: 'text-white hover:text-white/80',
				star: 'text-yellow-400',
				arrow: 'bg-white/20 hover:bg-white/30 text-white',
				arrowDisabled: 'bg-white/10 text-white/30',
			};
		case 'snow':
		default:
			return {
				section: 'bg-snow',
				heading: 'text-blue-dark',
				subtext: 'text-gray-600',
				card: 'bg-white border-gray-100',
				cardText: 'text-gray-800',
				cardSubtext: 'text-gray-500',
				link: 'text-blue hover:text-blue-dark',
				star: 'text-yellow-400',
				arrow: 'bg-blue hover:bg-blue-dark text-white',
				arrowDisabled: 'bg-gray-200 text-gray-400',
			};
	}
};

// Star rating helper
function getStarRating(rating: number, maxStars: number = 5): ('full' | 'half' | 'empty')[] {
	const stars: ('full' | 'half' | 'empty')[] = [];
	const fullStars = Math.floor(rating);
	const hasHalfStar = rating - fullStars >= 0.25 && rating - fullStars < 0.75;
	const hasFullInsteadOfHalf = rating - fullStars >= 0.75;

	for (let i = 0; i < maxStars; i++) {
		if (i < fullStars) {
			stars.push('full');
		} else if (i === fullStars && hasFullInsteadOfHalf) {
			stars.push('full');
		} else if (i === fullStars && hasHalfStar) {
			stars.push('half');
		} else {
			stars.push('empty');
		}
	}

	return stars;
}

interface TestimonialsProps {
	data: {
		title?: string;
		background?: string;
		region?: string;
		testimonials?: {
			name: string;
			location?: string;
			rating?: number;
			text: string;
			date?: string;
			region?: string;
		}[];
	};
}

interface GoogleReview {
	author_name: string;
	rating: number;
	text: string;
	relative_time_description?: string;
}

interface GoogleReviewsData {
	rating: number;
	reviewCount: number;
	reviews: GoogleReview[];
	googleMapsUrl: string;
}

// Star rating component
function StarRating({ rating, className }: { rating: number; className?: string }) {
	const stars = getStarRating(rating);

	return (
		<div className={cn('flex gap-0.5', className)}>
			{stars.map((star, i) =>
				star === 'full' ? (
					<Star key={i} className="size-5 fill-current" />
				) : star === 'half' ? (
					<StarHalf key={i} className="size-5 fill-current" />
				) : (
					<Star key={i} className="size-5 opacity-30" />
				),
			)}
		</div>
	);
}

// Individual testimonial card with truncation
function TestimonialCard({
	testimonial,
	theme,
}: {
	testimonial: { name: string; text: string; rating?: number; location?: string };
	theme: ReturnType<typeof getTheme>;
}) {
	const [isExpanded, setIsExpanded] = useState(false);
	const rating = testimonial.rating || 5;
	const maxLength = 150; // Max characters before truncation
	const shouldTruncate = testimonial.text.length > maxLength;
	const displayText =
		shouldTruncate && !isExpanded ? testimonial.text.slice(0, maxLength).trim() + '...' : testimonial.text;

	// Get initials from name
	const initials = testimonial.name
		.split(' ')
		.map((n) => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);

	return (
		<div
			className={cn('p-6 rounded-xl border shadow-sm flex flex-col h-full min-w-[280px] md:min-w-[320px]', theme.card)}
		>
			{/* Quote text */}
			<p className={cn('text-base leading-relaxed flex-grow mb-4', theme.cardText)}>
				{displayText}
				{shouldTruncate && !isExpanded && (
					<button onClick={() => setIsExpanded(true)} className={cn('ml-1 text-sm font-medium underline', theme.link)}>
						lees verder
					</button>
				)}
			</p>

			{/* Author and rating */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4 border-t border-current/10">
				<div className="flex items-center gap-3">
					{/* Avatar initials */}
					<div className="size-10 rounded-full bg-blue/10 flex items-center justify-center text-sm font-medium text-blue">
						{initials}
					</div>
					<div className="flex flex-col">
						<span className={cn('font-medium', theme.cardText)}>{testimonial.name}</span>
						{testimonial.location && (
							<span className={cn('text-sm', theme.cardSubtext)}>{testimonial.location}</span>
						)}
					</div>
				</div>
				<div className="flex items-center gap-2">
					<span className={cn('text-sm font-medium', theme.cardSubtext)}>{rating * 2}/10</span>
					<StarRating rating={rating} className={theme.star} />
				</div>
			</div>
		</div>
	);
}

/**
 * Filters testimonials by region. When a block region is set:
 * 1. First take testimonials matching the region
 * 2. Fill remaining slots (up to maxCount) with testimonials that have no region set
 * When no block region is set, return all testimonials (up to maxCount).
 */
function filterTestimonialsByRegion<T extends { region?: string }>(
	testimonials: T[],
	blockRegion?: string,
	maxCount = 5,
): T[] {
	if (!blockRegion) {
		return testimonials.slice(0, maxCount);
	}

	const normalizedRegion = blockRegion.toLowerCase().trim();
	const regionMatched = testimonials.filter((t) => t.region?.toLowerCase().trim() === normalizedRegion);
	const noRegion = testimonials.filter((t) => !t.region);

	// Region-matched first, then fill with generic (no-region) testimonials
	const result = [...regionMatched];
	if (result.length < maxCount) {
		result.push(...noRegion.slice(0, maxCount - result.length));
	}

	return result.slice(0, maxCount);
}

export default function Testimonials({ data }: TestimonialsProps) {
	const title = data?.title || 'Onze klanten zijn dik tevreden';
	const background = data?.background;
	const blockRegion = data?.region;
	const theme = getTheme(background);
	const cmsTestimonials = data?.testimonials || [];

	// Embla carousel
	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'start',
		containScroll: 'trimSnaps',
		dragFree: true,
	});

	const [canScrollPrev, setCanScrollPrev] = useState(false);
	const [canScrollNext, setCanScrollNext] = useState(false);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on('select', onSelect);
		emblaApi.on('reInit', onSelect);

		return () => {
			emblaApi.off('select', onSelect);
			emblaApi.off('reInit', onSelect);
		};
	}, [emblaApi, onSelect]);

	// Use shared Google Reviews context
	const googleData = useGoogleReviews();

	// Filter to only 5-star reviews and take up to 5
	const fiveStarReviews = googleData.reviews.filter((r) => r.rating === 5).slice(0, 5);

	// Use CMS testimonials if available, otherwise use Google reviews
	// Apply region filtering when block has a region set
	const displayTestimonials =
		cmsTestimonials.length > 0
			? filterTestimonialsByRegion(cmsTestimonials, blockRegion)
			: fiveStarReviews.map((review) => ({
					name: review.author_name,
					text: review.text,
					rating: review.rating,
				}));

	return (
		<section className={cn('py-16 md:py-24', theme.section)}>
			<Container>
				<div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
					{/* Left Column: Header & Google Stats */}
					<div className="lg:col-span-4 flex flex-col">
						<h2 className={cn('text-3xl md:text-4xl font-heading font-bold mb-4', theme.heading)}>
							{parseHeading(title)}
						</h2>

						<p className={cn('text-lg mb-6', theme.subtext)}>Klanten beoordelen ons met:</p>

						{/* Google Rating Display */}
						<div className="flex items-center gap-4 mb-2">
							<span className={cn('text-4xl font-bold', theme.heading)}>{googleData.rating.toFixed(1)}</span>
							<StarRating rating={googleData.rating} className={theme.star} />
						</div>

						<p className={cn('text-sm mb-6', theme.subtext)}>{googleData.reviewCount} beoordelingen</p>

						{/* Link to Google Reviews */}
						<a
							href={googleData.googleMapsUrl}
							target="_blank"
							rel="noopener noreferrer"
							className={cn(
								'inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4 transition-colors',
								theme.link,
							)}
						>
							Bekijk meer beoordelingen
							<ExternalLink className="size-4" />
						</a>
					</div>

					{/* Right Column: Scrollable Testimonial Cards */}
					<div className="lg:col-span-8 overflow-hidden">
						{/* Desktop Navigation Arrows - Top Right */}
						<div className="hidden md:flex items-center justify-end gap-2 mb-4">
							<button
								onClick={scrollPrev}
								disabled={Boolean(!canScrollPrev)}
							suppressHydrationWarning
								className={cn('p-2 rounded-full transition-colors', canScrollPrev ? theme.arrow : theme.arrowDisabled)}
								aria-label="Vorige review"
							>
								<ChevronLeft className="size-5" />
							</button>
							<button
								onClick={scrollNext}
								disabled={Boolean(!canScrollNext)}
							suppressHydrationWarning
								className={cn('p-2 rounded-full transition-colors', canScrollNext ? theme.arrow : theme.arrowDisabled)}
								aria-label="Volgende review"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>

						{/* Carousel */}
						<div className="overflow-hidden" ref={emblaRef}>
							<div className="flex gap-4">
								{displayTestimonials.map((testimonial, index) => (
									<div key={index} className="flex-shrink-0 w-[85%] md:w-[calc(50%-8px)]">
										<TestimonialCard testimonial={testimonial} theme={theme} />
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</Container>
		</section>
	);
}
